import { useState, useEffect } from "react";
import PropTypes from 'prop-types';
import { Button } from "react-materialize";
import { useUserDataContext } from '../../contexts/userDataProvider';
import { usePropertyContext } from '../../contexts/propertyProvider';
import ModalComponent from '../common/ModalComponent';
import settingsAPI from '../../services/settingsAPI';
import ordersAPI from '../../services/ordersAPI';
import userApi from "../../services/userAPI";
import coworkerApi from "../../services/coworkerAPI";
import { useRemove } from '../common/requests.hooks';
import { logOutUrl } from '../common/utils';

const RemoveAccount = ({ setIsLoading, setLocalError, workSpaceEmail }) => {
  const {
    data: propertyData
  } = usePropertyContext();
  const { translate } = useUserDataContext();

  const [isRemoveDataModal, setIsRemoveDataModal] = useState(false);
  const [isRemoveAccountModal, setIsRemoveAccountModal] = useState(false);

  const handleReload = () => {
    window.location.reload();
  };

  const removeOrdersCallback = () => {
    setIsRemoveDataModal(false);
    handleReload(); // Can't use "reloadPropertiesData();" from useUserDataContext, because TimeLine doesn't detect changes in groups array
  };

  const removeAccountCallback = () => {
    window.location.href = logOutUrl;
  };

  // TODO: display confirm message based on response
  const {
    sendRemoveRequestAsync: removeOrders,
    isLoading: isRemovingOrders,
    error: removeOrdersError
  } = useRemove(
    ordersAPI.removeAllOrders
  );

  const {
    sendRemoveRequestAsync: removeProperties,
    isLoading: isRemovingProperties,
    error: removePropertiesError
  } = useRemove(
    settingsAPI.removeAllProperties
  );

  const {
    sendRemoveRequestAsync: removeAccountRequest,
    isLoading: isRemovingAccount,
    error: removeAccountError
  } = useRemove(userApi.removeAccount);

  const {
    sendRemoveRequestAsync: removeCoworkerRequests,
    isLoading: isRemovingCoworkers,
    error: removeCoworkerRequestsError
  } = useRemove(coworkerApi.removeAllCoworkerRequests);

  const removeData = async () => {
    await removeOrders();
    await removeProperties();
    removeOrdersCallback();
  };

  const removeAccount = async () => {
    await removeAccountRequest();
    await removeCoworkerRequests();
    removeAccountCallback();
  };

  const isAnyRemoving =
    isRemovingAccount ||
    isRemovingCoworkers ||
    isRemovingOrders ||
    isRemovingProperties;

  const combinedError =
    removeOrdersError ||
    removePropertiesError ||
    removeAccountError ||
    removeCoworkerRequestsError;

  useEffect(() => {
    setIsLoading(isAnyRemoving);
  }, [isAnyRemoving, setIsLoading]);

  useEffect(() => {
    if (combinedError) {
      setLocalError(combinedError);
    }
  }, [combinedError, setLocalError]);

  return (
    <div className="remove-account-wrap">
      <h5>{translate('removeAccount')}</h5>
      <ModalComponent 
        ModalContent={
          isRemoveAccountModal
          ? translate('yourAccountWillBeRemoved')
          : translate('allRecordsWillBeRemoved')
        }
        onSubmitButtonHandler={isRemoveAccountModal ? removeAccount : removeData}
        onSubmitButtonName={translate('yesRemove')}
        show={isRemoveDataModal || isRemoveAccountModal}
        handleClose={() => {
          if (isRemoveAccountModal) {
            setIsRemoveAccountModal(false);
          } else {
            setIsRemoveDataModal(false);
          }
        }}
        type="WARNING"
      />
      {workSpaceEmail && <p className="remove-account-warning">{translate('removeAcceptedWorkspaceToContinue')}</p> }
      <Button
        node="button"
        waves="light"
        onClick={() => setIsRemoveDataModal(true)}
        disabled={workSpaceEmail || !propertyData?.length}
      >
        {translate('removeAllRecords')}
      </Button>
      <Button
        className="remove-account-btn"
        node="button"
        waves="light"
        onClick={() => setIsRemoveAccountModal(true)}
        disabled={!!propertyData?.length}
      >
        {translate('removeAccount')}
      </Button>
    </div>
  )
};

RemoveAccount.propTypes = {
  setIsLoading: PropTypes.func.isRequired,
  setLocalError: PropTypes.func.isRequired,
  workSpaceEmail: PropTypes.string
};

export default RemoveAccount;
